<template>
  <div
    class="rounded-lg md:h-[200px] my-4 p-1 lg:w-1/4 md:w-2/4 w-4/4 relative cursor-pointer group focusable"
    tabindex="0"
    @keydown.enter="$router.push({ name: 'watch', params: { id: item.id } })"
    @click="$router.push({ name: 'watch', params: { id: item.id } })"
  >
    <!-- Overlay: Visible on hover or focus -->

    <div class="absolute bottom-0 inset-x-0">
      <div
        class="absolute bottom-1 inset-x-1 h-64 bottom-shadow-half bg-opacity-50 opacity-0 text-white"
      ></div>
      <!-- Title -->
      <div ref="titleBox" class="absolute bottom-6 right-6 w-1/2 overflow-hidden">
        <span
          ref="titleSpan"
          class="text-white text-xl font-medium whitespace-nowrap marquee-item"
          :class="{ 'is-overflowing': isOverflowing }"
          :style="marqueeStyle"
        >
          {{ item.title }}
        </span>
      </div>

      <!-- Index -->
      <div class="absolute bottom-6 left-6" v-show="index >= 0">
        <p class="text-white text-right text-7xl font-medium">
          {{ index < 9 ? `0${index + 1}` : index + 1 }}
        </p>
      </div>
    </div>

    <!-- Image -->
    <img
      ref="image"
      class="rounded-lg w-full h-full object-cover"
      :src="defaultImage"
      :data-src="imageData(item.image)"
      :alt="item.title"
    />
  </div>
</template>

<script>
import { S3_BASE_URL } from "@/constants";

export default {
  components: {},
  props: ["item", "index"],
  data() {
    return {
      type: "eposides",
      S3_BASE_URL: S3_BASE_URL,
      defaultImage: require("@/assets/placeholder.webp"),
      observer: null,
      isOverflowing: false,
      marqueeDistance: 0,
      marqueeDuration: 0,
    };
  },
  computed: {
    marqueeStyle() {
      if (!this.isOverflowing) return {};
      return {
        "--marquee-distance": `${this.marqueeDistance}px`,
        animationDuration: `${this.marqueeDuration}s`,
      };
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(this.handleIntersection, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    this.observer.observe(this.$refs.image);

    this.$nextTick(this.checkTitleOverflow);
    window.addEventListener("resize", this.checkTitleOverflow);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkTitleOverflow);
  },
  methods: {
    checkTitleOverflow() {
      const box = this.$refs.titleBox;
      const span = this.$refs.titleSpan;
      if (!box || !span) return;

      const overflow = span.scrollWidth - box.clientWidth;
      this.isOverflowing = overflow > 0;
      if (this.isOverflowing) {
        this.marqueeDistance = overflow;
        // ~60px/s scroll speed, plus a fixed budget for the start/end pauses
        this.marqueeDuration = Math.max(3, overflow / 60 + 2.5);
      }
    },
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          this.observer.unobserve(img);
        }
      });
    },
    imageData(image) {
      if(!image) return this.defaultImage;
      return `${this.S3_BASE_URL}${this.type}/${image}`;
    },
  },
};
</script>

<style scoped>
.swiper {
  width: 100%;
  height: 308px;
}

@media only screen and (max-width: 960px) {
  .swiper {
    width: 100%;
    height: 168px;
  }

  /* Hide navigation buttons on non-desktop screens */
  .swiper-button-prev,
  .swiper-button-next {
    display: none;
  }
}

.swiper-slide {
  text-align: center;
  font-size: 18px;
  width: 480px !important;
  height: 100%;
  height: 100%;
}

@media only screen and (max-width: 960px) {
  .swiper-slide {
    text-align: center;
    width: 240px !important;
    height: 100%;
  }
}

.swiper-slide img {
  display: block;
  width: 100%;
  height: 100%;
  -o-object-fit: none;
  object-fit: fill !important;
}

/* Navigation buttons styles */
.swiper-button-prev,
.swiper-button-next {
  color: #fff;
  /* Change the color to suit your design */
}

/* Navigation buttons styles with overlay */
.swiper-button-prev,
.swiper-button-next {
  position: absolute;
  top: 45%;
  z-index: 99999;
  font-size: 20px !important;
  width: 80px;
  /* Width of the arrow button */
  height: 80px;
  /* Height of the arrow button */
  margin-top: -25px;
  /* Half of the height to center vertically */
  background-color: rgba(0, 0, 0, 0.5);
  /* Semi-transparent black background */
  color: #fff;
  /* White arrow color */
  border-radius: 50%;
  /* Circular shape */
}

.swiper-button-next::after,
.swiper-button-prev::after {
  font-size: 20px !important;
  text-transform: none !important;
  letter-spacing: 0;
  font-variant: initial;
  line-height: 1;
}

.swiper-button-prev.swiper-button-disabled,
.swiper-button-next.swiper-button-disabled {
  opacity: 0 !important;
  cursor: auto;
  pointer-events: none;
}
.focusable:focus {
  outline: none;
}

.focusable:focus,
.focusable:hover {
  border-radius: 8px;
  background-color: white;
}

/* Static by default; only animates once JS confirms the title overflows its 50%-wide box */
.marquee-item {
  display: inline-block;
}

.marquee-item.is-overflowing {
  animation-name: marquee-scroll;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes marquee-scroll {
  0%,
  10% {
    transform: translateX(0);
  }
  90%,
  100% {
    transform: translateX(calc(-1 * var(--marquee-distance, 0px)));
  }
}
</style>
